import { useMemo } from "react";
import Ico from "../../components/Ico";

export type NpDoneVariant = "registered" | "welcome" | "pending";

interface NpDoneProps {
  variant: NpDoneVariant;
  onGoHome: () => void;
  /** 按鈕文字（依落點而異：傳單→「前往主頁」、QR→「前往下單」）。預設「前往主頁」。 */
  cta?: string;
}

const COLORS = ["#ffffff", "#BBDEFB", "#FFE082", "#A5D6A7", "#FFAB91", "#CE93D8"];

const CONTENT: Record<
  NpDoneVariant,
  { title: string; note: string; confetti: boolean; icon: "check" | "clock"; grad: string }
> = {
  registered: { title: "完成加入！", note: "歡迎加入，開始線上叫瓦斯", confetti: true, icon: "check", grad: "linear-gradient(145deg,#34D399,#10B981)" },
  welcome: { title: "歡迎回來！", note: "很高興再次為您服務", confetti: true, icon: "check", grad: "linear-gradient(145deg,#34D399,#10B981)" },
  // 誠實收尾（推薦也查無合作瓦斯行）：不灑花、琥珀時鐘（與訂單「待確認」同色彙），
  // 先肯定完成的部分（註冊）、再照實說沒完成的部分（配對），下方以進度卡交代下一步。
  pending: {
    title: "註冊完成，正在為您安排瓦斯行",
    note: "您附近目前還沒有合作瓦斯行。您的資料已登記，平台安排好合作瓦斯行後，會第一時間通知您。",
    confetti: false,
    icon: "clock",
    grad: "linear-gradient(145deg,#FBBF24,#F59E0B)",
  },
};

/** pending 進度卡的三步驟（✓ 完成 → 進行中 → 待開始） */
const PENDING_STEPS: Array<{ title: string; desc: string; state: "done" | "now" | "todo" }> = [
  { title: "完成註冊", desc: "帳號已建立，資料已收到", state: "done" },
  { title: "安排合作瓦斯行", desc: "平台正在為您的地區安排合作瓦斯行", state: "now" },
  { title: "開始訂購瓦斯", desc: "安排完成後即可一鍵叫瓦斯", state: "todo" },
];

const FEATURES: Array<{ icon: Parameters<typeof Ico>[0]["name"]; title: string; desc: string }> = [
  { icon: "tank", title: "一鍵訂購", desc: "選規格、時間直接下單" },
  { icon: "truck", title: "配送追蹤", desc: "即時看訂單進度" },
  { icon: "bell", title: "貼心提醒", desc: "快用完主動通知" },
  { icon: "receipt", title: "帳戶發票", desc: "付款、發票、優惠券" },
];

/** 傳單・無指定瓦斯行完成頁：漸層 + 徽章 + 功能磁磚。 */
const NpDone = ({ variant, onGoHome, cta }: NpDoneProps) => {
  const c = CONTENT[variant];
  const pieces = useMemo(
    () =>
      Array.from({ length: 44 }).map((_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        dur: 2 + Math.random() * 1.8,
        size: 6 + Math.random() * 7,
        color: COLORS[i % 6],
        round: i % 2 === 0,
      })),
    []
  );

  return (
    <div className="h-full w-full flex flex-col relative overflow-hidden bg-[#2196f3]">
      {c.confetti && (
        <div className="absolute inset-x-0 top-0 h-[42%] pointer-events-none z-0 overflow-hidden">
          {pieces.map((p, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                top: -14,
                left: `${p.left}%`,
                width: p.size,
                height: p.round ? p.size : p.size * 0.5,
                background: p.color,
                borderRadius: p.round ? "50%" : 2,
                animation: `onb-cfFall ${p.dur}s ease-in ${p.delay}s infinite`,
              }}
            />
          ))}
        </div>
      )}

      {/* 藍色頂區（拉高 → 白色 sheet 往下） */}
      <div className="shrink-0 text-center text-white pt-[60px] pb-[110px] relative z-10">
        {/* 品牌小標移除。保留同樣式的空行（&nbsp;）而非寫死高度：行高完全等同原本那行，
            藍色頂區高度與跨界徽章位置不會位移。 */}
        <div className="text-[11px] font-extrabold tracking-[5px] opacity-80" aria-hidden>
          &nbsp;
        </div>
      </div>

      {/* 白色圓角 sheet：overflow 保持可見，讓徽章(-mt) 能跨在藍/白交界不被裁；捲動移到內層。 */}
      <div className="flex-1 min-h-0 bg-white rounded-t-[32px] relative z-10 flex flex-col">
        {/* 徽章（跨在藍/白交界；置於捲動容器之外，避免 overflow 裁切上緣） */}
        <div className="flex justify-center -mt-[52px] mb-3 shrink-0">
          <div className="w-[96px] h-[96px] rounded-full bg-white flex items-center justify-center onb-pop" style={{ boxShadow: "0 14px 34px -10px rgba(33,150,243,.55)" }}>
            <div className="w-[70px] h-[70px] rounded-full flex items-center justify-center" style={{ background: c.grad }}>
              {c.icon === "check" ? (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" stroke="#fff" strokeWidth="2" />
                  <path d="M12 7.5V12l3 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* 內容捲動區（pending 內容較高，小螢幕可捲；徽章在此容器之外不受裁切） */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-6 pb-8">
        <div className="text-center">
          <div className="text-[24px] font-black text-[#0f1a35]">{c.title}</div>
          {c.note && <div className="text-[13.5px] text-[#64748B] leading-[1.6] mt-1.5 px-2">{c.note}</div>}
        </div>

        {variant === "pending" ? (
          <>
            {/* 三步進度卡：功能磁磚在「還沒配到瓦斯行」情境是反效果（點了也叫不了瓦斯），
                改讓客戶知道現在卡在哪一步、不是自己操作錯誤。 */}
            <div className="bg-[#F6F9FE] border border-[#EAF1FB] rounded-[16px] px-4 pt-4 pb-3 mt-5">
              {PENDING_STEPS.map((s, i) => (
                <div key={s.title} className="flex gap-3">
                  <div className="flex flex-col items-center w-[22px] shrink-0">
                    {s.state === "done" ? (
                      <div className="w-[22px] h-[22px] rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[12px] font-black shrink-0 ${
                          s.state === "now" ? "bg-[#FEF3C7] border-2 border-[#F59E0B] text-[#B45309]" : "bg-[#EDF2F9] text-[#94A3B8]"
                        }`}
                      >
                        {i + 1}
                      </div>
                    )}
                    {i < PENDING_STEPS.length - 1 && (
                      <div className={`w-[2px] flex-1 min-h-[14px] my-[3px] ${s.state === "done" ? "bg-[#22C55E]" : "bg-[#E3ECF7]"}`} />
                    )}
                  </div>
                  <div className={i < PENDING_STEPS.length - 1 ? "pb-4 pt-px" : "pb-0.5 pt-px"}>
                    <div className={`text-[13.5px] font-extrabold ${s.state === "todo" ? "text-[#94A3B8]" : "text-[#0f1a35]"}`}>
                      {s.title}
                      {s.state === "now" && (
                        <span className="inline-block ml-1.5 text-[10.5px] font-extrabold text-[#B45309] bg-[#FEF3C7] rounded-full px-2 py-0.5 align-[1px]">
                          進行中
                        </span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-[#94A3B8] mt-0.5 leading-snug">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            {/* LINE 通知提示列：回答「要等多久／要不要再來看」，並防重複註冊 */}
            <div className="flex items-center gap-2.5 bg-[#F0FAF4] border border-[#D7F0E1] rounded-[14px] px-3.5 py-3 mt-3">
              <div className="w-[30px] h-[30px] rounded-[9px] bg-[#22C55E] flex items-center justify-center shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 4c-4.4 0-8 2.9-8 6.5 0 2.1 1.2 3.9 3.1 5.1-.1.5-.5 1.7-.6 2 0 0-.1.3.1.4.2.1.4 0 .4 0 .5-.1 2.2-1 3.1-1.5.6.1 1.2.2 1.9.2 4.4 0 8-2.9 8-6.5S16.4 4 12 4z" fill="#fff" />
                </svg>
              </div>
              <div className="text-[12px] text-[#1B7A46] leading-snug font-semibold">安排完成會透過 LINE 訊息通知您，不需要重新註冊。</div>
            </div>
          </>
        ) : (
          <>
            <div className="text-[12.5px] font-bold text-[#94A3B8] text-center mt-6 mb-3">現在就可以使用這些功能</div>
            <div className="grid grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-[#F6F9FE] border border-[#EAF1FB] rounded-[16px] p-3.5">
                  <div className="w-9 h-9 rounded-[11px] bg-[#E7F1FF] flex items-center justify-center mb-2">
                    <Ico name={f.icon} c="#2196f3" s={20} />
                  </div>
                  <div className="text-[13.5px] font-bold text-[#0f1a35]">{f.title}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug">{f.desc}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex-1" />

        <button
          onClick={onGoHome}
          className="w-full py-4 rounded-[16px] border-none text-white text-base font-bold cursor-pointer mt-6 bg-[#2196f3]"
        >
          {cta ?? "前往主頁"}
        </button>
        </div>
      </div>
    </div>
  );
};

export default NpDone;
